import { useEffect, useRef, forwardRef, useContext } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Customized,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
} from "recharts"
import intl from "react-intl-universal"
import { ReactComponent as ICheck } from "../../../../../../../assets/icons/iconCheck.svg"
import { ReactComponent as IBlocked } from "../../../../../../../assets/icons/circle-blocked.svg"
import { ReactComponent as IEmpty } from "../../../../../../../assets/icons/circle-empty.svg"
import { ReactComponent as IAlertRegular } from "../../../../../../../assets/icons/alert-regular.svg"
import "./styles.css"
import styles from "./index.module.scss"
import { fnFormatDateFromUTC } from "../../../../../../../utils/date"
import { CONTROL_LEVELS } from "../../../../constants"
import AuthContext from "../../../../../../../contexts/Auth"

const CHART_Y_AXIS_TICKS = ["-4s", "-3s", "-2s", "-1s", "xm", "1s", "2s", "3s", "4s"]
const WESTGARD_EACTION = {
  A: "A",
  R: "R",
}
const WESTGARD_STATUS_COLOR = {
  A: "#F5F0C9",
  R: "#F5CFD3",
}

const convertDeviation = (value) => {
  if (value === undefined || value === null || typeof value !== "number") return value

  if (value >= 0 && value < 1) return "xm"
  return `${value.toFixed(0)}s`
}

const transformDate = (date, cUserTimeZone) => {
  const [data, hour] = fnFormatDateFromUTC(
    `${date}`,
    "DD/MM/YYYY - HH:mm",
    cUserTimeZone,
  ).split(" - ")

  return `${data} - ${hour}`
}

const Chart = forwardRef(
  (
    {
      runs,
      chartData,
      selectedChartRun,
      nSpaceBetweenVerticalLines = 92,
      handleOnClickChart,
      runsWestardStatus,
      bLineAnimationActive = false,
    },
    ref,
  ) => {
    const chartMargin = {
      top: 32,
      right: (nSpaceBetweenVerticalLines + 2) / 2,
      left: nSpaceBetweenVerticalLines / 2,
      bottom: 8,
    }

    const groupChartLines = () => {
      if (!chartData?.length) return []

      return chartData.reduce((aLines, oValues) => {
        const aValueKeys = Object.keys(oValues)

        for (const key of aValueKeys) {
          const match = fnFindLevelAndLineByDataKey(key) // find chartData value key

          if (match) {
            if (!aLines?.find((v) => v?.dataKey === key)) {
              const nLevel = match.nLevel
              const nLine = match.nLine

              const levelProp = CONTROL_LEVELS.find((c) => c.level === nLevel)
              // add new line
              aLines.push({
                dataKey: key,
                nLine,
                nLevel,
                cColor: levelProp.color,
              })
            }
          }
        }

        return aLines
      }, [])
    }

    const chartDataLength = chartData?.length

    return (
      <ResponsiveContainer height={250} className="larey-jennings-chart">
        <LineChart
          ref={ref}
          data={chartData}
          onClick={handleOnClickChart}
          margin={chartMargin}
        >
          {runsWestardStatus && (
            <Customized
              component={
                <ChartAlert
                  chartData={chartData}
                  nSpaceBetweenVerticalLines={nSpaceBetweenVerticalLines}
                  runsWestardStatus={runsWestardStatus}
                />
              }
            />
          )}
          <CartesianGrid
            stroke="#CCD1D6"
            strokeWidth={1}
            strokeDasharray="0"
            horizontal={
              !chartDataLength ? null : (
                <CustomHorizontalLineGrid
                  nSpaceBetweenVerticalLines={nSpaceBetweenVerticalLines}
                  chartMargin={chartMargin}
                />
              )
            }
            vertical={
              <CustomVerticalLineGrid
                nSpaceBetweenVerticalLines={nSpaceBetweenVerticalLines}
                chartData={chartData}
                runs={runs}
              />
            }
          />
          {selectedChartRun && (
            <Customized
              component={
                <SelecetdRunPlaceholder
                  selectedChartRun={selectedChartRun}
                  chartData={chartData}
                  nSpaceBetweenVerticalLines={nSpaceBetweenVerticalLines}
                />
              }
            />
          )}

          <XAxis
            axisLine={false}
            dataKey="run"
            tick={<CustomXAxisTick />}
            interval={0}
            stroke="#CCD1D6"
            allowDataOverflow={false}
            allowDuplicatedCategory={false}
            orientation="bottom"
          />
          <YAxis
            axisLine={false}
            dataKey="yAxis"
            type="number"
            stroke="#CCD1D6"
            allowDuplicatedCategory={false}
            allowDataOverflow={false}
            interval={0}
            tickCount={9}
            domain={[-4, 4]}
            tickLine={false}
            tick={
              <CustomYAxisTick nSpaceBetweenVerticalLines={nSpaceBetweenVerticalLines} />
            }
          />

          {groupChartLines()?.map((chartLine, index) => (
            <Line
              key={`${chartLine.dataKey}-${index}`}
              type="linear"
              dataKey={chartLine.dataKey}
              stroke={chartLine.cColor}
              strokeWidth={3}
              animationDuration={700}
              animationEasing="ease-out"
              isAnimationActive={bLineAnimationActive}
              dot={
                <CustomLineDot
                  colorCenterDot={chartLine.cColor}
                  dataKey={chartLine.dataKey}
                  runs={runs}
                />
              }
            />
          ))}
          <Tooltip content={<CustomTooltip runs={runs} />} />
        </LineChart>
      </ResponsiveContainer>
    )
  },
)

export default Chart

const CustomTooltip = (props) => {
  const { label, runs } = props
  const authContext = useContext(AuthContext)

  if (!runs?.length) return null

  const cUserTimeZone = authContext?.oUser?.data?.user_ctimezone

  const rolesSelected = runs.find((it) => it.eqpanltrun_number === label)
  const rules = rolesSelected?.eqpanltrun_launches.map((item) => [
    ...item.eqpanltlaunch_awestgard_rules,
    {
      eqpanltlaunch_dcreated: item.eqpanltlaunch_dcreated,
      eqpanltlaunch_user: item.eqpanltlaunch_user,
    },
  ])[0]

  return !rules?.some((it) => it.eqpanltlnchwgr_eaction) ? (
    <div className={styles.customToolTioEmpty}>
      <p className={styles.title}>{intl.get("ciq.any_alert_and_reject")}</p>
    </div>
  ) : (
    <div className={styles.customToolTip}>
      {rules?.some((it) => it.eqpanltlnchwgr_type === "intra") && (
        <>
          {rules?.map((it) => {
            if (it.eqpanltlaunch_dcreated) {
              return (
                <div key={it.eqpanltlaunch_dcreated} className={styles.userAndDate}>
                  <span>{it.eqpanltlaunch_user?.cname}</span>
                  <span>{transformDate(it.eqpanltlaunch_dcreated, cUserTimeZone)}</span>
                </div>
              )
            }
          })}
          <p className={styles.title}>Mesmo nível</p>
          <div>
            {rules?.map((it) => {
              if (it.eqpanltlnchwgr_type === "intra") {
                return (
                  <strong key={it.eqpanltlnchwgr_wgrdr_cname}>
                    {it.eqpanltlnchwgr_wgrdr_cname}
                  </strong>
                )
              }
            })}
          </div>
        </>
      )}

      {rules?.some((it) => it.eqpanltlnchwgr_type === "inter") && (
        <>
          <p className={styles.title}>Entre níveis</p>
          <div>
            {rules?.map((it) => {
              if (it.eqpanltlnchwgr_type === "inter") {
                return (
                  <strong key={it.eqpanltlnchwgr_wgrdr_cname}>
                    {it.eqpanltlnchwgr_wgrdr_cname}
                  </strong>
                )
              }
            })}
          </div>
        </>
      )}
    </div>
  )
}

const CustomYAxisTick = (props) => {
  const { y, payload } = props
  const index = convertDeviation(payload?.value)
  const tick = CHART_Y_AXIS_TICKS.find((e) => e === index)

  return (
    <g transform={`translate(${25},${y - 13})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#415364"
        fontWeight="600"
        fontSize="12px"
      >
        {tick}
      </text>
    </g>
  )
}

const CustomXAxisTick = (props) => {
  const { x, y, payload } = props

  if (payload?.value < 0) return null

  return (
    <g transform={`translate(${x}, ${y + 13})`}>
      <text textAnchor="middle" fill="#415364" fontWeight="600" fontSize="12px">
        {payload.value}
      </text>
    </g>
  )
}

const CustomHorizontalLineGrid = (props) => {
  const { nSpaceBetweenVerticalLines, offset, x1, x2, chartMargin, ...rest } = props

  return <line x1={x1 - chartMargin.right} x2={x2 + chartMargin.right} {...rest}></line>
}

const CustomVerticalLineGrid = (props) => {
  const { nSpaceBetweenVerticalLines, chartData, runs, x2, offset, y1, index, ...rest } =
    props

  const lineRun = chartData?.[index]
  const run = runs?.find((run) => run.eqpanltrun_number === lineRun?.run)

  const lineY1 = y1 - 6
  if (lineRun?.mockLine) {
    return <line x2={x2} y1={lineY1} {...rest} />
  }

  if (!run) return null

  return (
    <>
      <g transform={`translate(${x2 - 7},${y1 - 26})`}>
        <CustomXAxisTopTick run={run} />
      </g>
      <line x2={x2} y1={lineY1} {...rest} />
    </>
  )
}

const CustomXAxisTopTick = ({ run }) => {
  const iconRef = useRef(null)
  const alertIconRef = useRef(null)
  const statusRef = useRef(run?.eqpanltrun_status?.toUpperCase())
  const status = run?.eqpanltrun_status?.toUpperCase()

  const parametersChanged = run?.eqpanltrun_parameters_changed
  const statusIcons = {
    PENDING: <IEmpty width={15} height={15} />,
    REJECTED: <IBlocked width={15} height={15} />,
    APPROVED: <ICheck width={15} height={15} />,
  }

  const statusTexts = {
    PENDING: intl.get("ciq.status_race.pending"),
    REJECTED: intl.get("ciq.status_race.disapproved"),
    APPROVED: intl.get("ciq.status_race.approved"),
  }

  const elStatusIcons = statusIcons[status] || null
  const translateX = parametersChanged ? 10 : 0

  useEffect(() => {
    if (!run?.eqpanltrun_nid) {
      statusRef.current = null
      return
    }

    statusRef.current = run.eqpanltrun_status?.toUpperCase()
  }, [run?.eqpanltrun_nid])

  useEffect(() => {
    if (!iconRef?.current) return

    iconRef.current.addEventListener("mouseover", (e) =>
      handleOnShowTooltip(e, "run-status-tooltip", null, parametersChanged ? 22 : 20),
    )
    iconRef.current.addEventListener("mouseleave", () =>
      handleOnHideTooltip("run-status-tooltip"),
    )

    return () => {
      if (iconRef?.current) {
        iconRef.current.removeEventListener("mouseover", handleOnShowTooltip)
        iconRef.current.removeEventListener("mouseleave", handleOnHideTooltip)
        handleOnHideTooltip("run-status-tooltip")
      }
    }
  }, [iconRef])

  useEffect(() => {
    if (!alertIconRef?.current || !parametersChanged) return

    alertIconRef.current.addEventListener("mouseover", (e) =>
      handleOnShowTooltip(e, "run-alert-tooltip", intl.get("ciq.status_race.alert"), 8),
    )
    alertIconRef.current.addEventListener("mouseleave", () =>
      handleOnHideTooltip("run-alert-tooltip"),
    )

    return () => {
      if (alertIconRef?.current) {
        alertIconRef.current.removeEventListener("mouseover", handleOnShowTooltip)
        alertIconRef.current.removeEventListener("mouseleave", handleOnHideTooltip)
        handleOnHideTooltip("run-alert-tooltip")
      }
    }
  }, [alertIconRef, parametersChanged])

  const handleOnShowTooltip = (e, textClass, cStatus, positionX) => {
    if (document.getElementsByClassName(textClass)?.length) return
    if (!e || !e?.target?.getBoundingClientRect()) return

    const { x, y } = e.target.getBoundingClientRect()
    const elAlertText = document.createElement("p")
    elAlertText.innerText = cStatus || statusTexts[statusRef.current]
    elAlertText.classList.add(textClass)
    elAlertText.classList.add(styles.statusTooltip)
    elAlertText.style.top = `${y - 22}px`
    elAlertText.style.left = `${x - positionX}px`
    document.body.appendChild(elAlertText)
  }

  const handleOnHideTooltip = (textClass) => {
    const elToolTipAlert = document.getElementsByClassName(textClass)
    if (!elToolTipAlert.length) return

    for (const el of elToolTipAlert) {
      el.remove()
    }
  }

  return (
    <>
      <g transform={`translate(-${translateX}, 0)`}>
        {elStatusIcons}
        <rect ref={iconRef} fill="transparent" width={15} height={15}></rect>
      </g>
      {parametersChanged && (
        <g transform={`translate(${translateX}, -1)`}>
          <IAlertRegular />
          <rect ref={alertIconRef} fill="transparent" width={15} height={15}></rect>
        </g>
      )}
    </>
  )
}

const CustomLineDot = (props) => {
  const { cx, cy, colorCenterDot, dataKey, payload, runs } = props

  if (typeof payload?.[dataKey] !== "number") return null

  const match = fnFindLevelAndLineByDataKey(dataKey) // find chartData value key
  const nLevel = match?.nLevel

  let isOnTop = false
  let onTopIndex = 0

  // check if the dot is on top of another dot
  if (nLevel > 1) {
    const currentRun = runs?.find((run) => run?.eqpanltrun_number === payload?.run)
    for (let i = nLevel; i > 1; i--) {
      const launchValue = payload?.[dataKey]
      const prevLaunchLevelValue = currentRun?.eqpanltrun_launches?.find(
        (launch) => launch.eqpanltlaunch_elevel === i - 1,
      )?.eqpanltlaunch_ndeviation

      const nLaunchesDiff = Math.abs(launchValue - prevLaunchLevelValue)
      if (nLaunchesDiff === 0 || nLaunchesDiff <= 0.1) {
        onTopIndex++
        isOnTop = true
      }
    }
  }
  const onTopRadiusIndexMap = {
    0: 7,
    1: 5,
    2: 3,
  }

  const opacity = isOnTop ? 0.8 : 1
  const radius = isOnTop ? onTopRadiusIndexMap[onTopIndex] : 7
  return (
    <g opacity={opacity}>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="#DBDFE5"
        stroke="#DBDFE5"
        strokeWidth={isOnTop ? 0 : 1}
        style={{
          boxShadow: "0px 2px 4px 0px #00000029",
        }}
      />
      <circle cx={cx} cy={cy} r={radius - 1} fill={colorCenterDot} />{" "}
    </g>
  )
}

const SelecetdRunPlaceholder = (props) => {
  const {
    selectedChartRun,
    formattedGraphicalItems,
    chartData,
    nSpaceBetweenVerticalLines,
  } = props
  if (!selectedChartRun) return null

  const chartMarks = chartMarkProperties(
    selectedChartRun,
    chartData,
    formattedGraphicalItems,
    nSpaceBetweenVerticalLines,
  )

  if (!chartMarks) return null

  const { positionX, placeHolderWidth } = chartMarks

  return (
    <Rectangle
      width={placeHolderWidth}
      x={positionX}
      height={180}
      y={32}
      fill="transparent"
      stroke="#22272D"
      strokeWidth={2}
    />
  )
}

const ChartAlert = (props) => {
  const {
    formattedGraphicalItems,
    chartData,
    runsWestardStatus,
    nSpaceBetweenVerticalLines,
  } = props
  if (!runsWestardStatus?.length) return null

  const filteredRuns = runsWestardStatus.reduce((arr, run) => {
    if (!arr?.find((r) => r.run === run.run)) {
      arr.push(run)
    } else {
      // prevent to show the alert if the run has a rejected status
      const index = arr.findIndex((r) => r.run === run.run)
      if (run.eaction === WESTGARD_EACTION.R) arr[index] = run
    }

    return arr
  }, [])

  return filteredRuns.map((westgard) => {
    const chartMarks = chartMarkProperties(
      westgard.run,
      chartData,
      formattedGraphicalItems,
      nSpaceBetweenVerticalLines,
    )
    if (!chartMarks) return null

    const { positionX, placeHolderWidth } = chartMarks
    return (
      <Rectangle
        key={`alert-run-${westgard.run}`}
        width={placeHolderWidth}
        x={positionX}
        height={180}
        y={32}
        fill={WESTGARD_STATUS_COLOR[westgard.eaction]}
      />
    )
  })
}

const chartMarkProperties = (
  selectedChartRun,
  chartData,
  formattedGraphicalItems,
  nSpaceBetweenVerticalLines,
) => {
  const selectedRun = chartData?.find((data) => data.run === selectedChartRun)
  if (!selectedRun) return null

  const chartXAxisPoints = formattedGraphicalItems?.[0]?.props?.points

  const selectedRunPointIndex = chartXAxisPoints?.findIndex(
    (point) => point?.payload?.run === selectedChartRun,
  )
  if (selectedRunPointIndex < 0) return null

  const selectedRunPoint = chartXAxisPoints?.[selectedRunPointIndex]
  if (!selectedRunPoint) return null

  let positionX = selectedRunPoint.x - nSpaceBetweenVerticalLines / 2
  let placeHolderWidth = nSpaceBetweenVerticalLines - 1

  return { positionX, placeHolderWidth }
}

const fnFindLevelAndLineByDataKey = (dataKey) => {
  const regex = /level-(\d+)-line-(\d+)/
  const match = dataKey.match(regex)

  if (!match) return null

  return {
    nLevel: Number(match[1]),
    nLine: Number(match[2]),
  }
}
